import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface MotorInvestigationApproval {
  id: number;
  motorInvestigationFlowId: number;
  region: string;
  ticketNumber: string;
  oldMotorPN: number;
  approvalStatus: string;
  approvalStatusId: number;
  initiator: string;
  dueDate: string;
  machineTypes: string;
  dueDateFormatted: string;
}

export interface MotorInvestigationApprovalResponse {
  totalResult: number;
  motorInvestigationApprovals: MotorInvestigationApproval[];
}

export interface MotorInvestigationApprovalInsights {
  inApprovalResult: number;
  approvedResult: number;
  weeklySentBackResult: number;
  fortnightlySentBackResult: number;
  monthlySentBackResult: number;
  yearlySentBackResult: number;
  weeklyApprovedResult: number;
  fortnightlyApprovedResult: number;
  monthlyApprovedResult: number;
  yearlyApprovedResult: number;
}

export interface FetchMotorInvestigationApprovalParams {
  page?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

export interface MotorInvestigationApprovalComment {
  id: number;
  motorInvestigationFlowId: number;
  comment: string;
  commentedBy: string;
  commentedByRole: string;
  createdAtUtc: string;
}

export interface SendApprovalSendBackRequest {
  motorInvestigationFlowId: number;
  comment: string;
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorInvestigationApprovalApiSlice = createApi({
  reducerPath: 'motorInvestigationApprovalApi',
  baseQuery: baseQuery,
  tagTypes: ['MotorInvestigationApprovalData', 'MotorInvestigationCommentsData', 'MotorInvestigationApprovalInsights'],
  endpoints(builders) {
    return {
      fetchMotorInvestigationApprovalList: builders.query<MotorInvestigationApprovalResponse, FetchMotorInvestigationApprovalParams>({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION_APPROVAL.SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationApprovalData'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorInvestigationApprovalListById: builders.query<MotorInvestigationApprovalResponse, number>({
        query: (id) => {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION_APPROVAL.SEARCH}/?Id=${id}`,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationApprovalData'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorInvestigationApprovalInsights: builders.query<MotorInvestigationApprovalInsights, void>({
        query: () => {
          return {
            url: API_URLS.MOTOR_INVESTIGATION_APPROVAL.INSIGHTS,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationApprovalInsights'],
        transformErrorResponse: transformApiError,
      }),
      fetchMotorInvestigationApprovalComments: builders.query<MotorInvestigationApprovalComment[], number>({
        query: (motorInvestigationFlowId) => {
          return {
            url: `${API_URLS.MOTOR_INVESTIGATION_APPROVAL.COMMENTS}?motorInvestigationFlowId=${motorInvestigationFlowId}`,
            method: 'GET',
          };
        },
        providesTags: ['MotorInvestigationCommentsData'],
        transformErrorResponse: transformApiError,
      }),
      approveInvestigationTicket: builders.mutation<number, { SendApprovalSendBackRequest: SendApprovalSendBackRequest }>({
        query({ SendApprovalSendBackRequest }) {
          return {
            url: API_URLS.MOTOR_INVESTIGATION_APPROVAL.APPROVE,
            method: 'POST',
            body: SendApprovalSendBackRequest,
          };
        },
        invalidatesTags: ['MotorInvestigationApprovalData', 'MotorInvestigationCommentsData', 'MotorInvestigationApprovalInsights'],
        transformErrorResponse: transformApiError,
      }),
      sendBackInvestigationTicket: builders.mutation<number, { SendApprovalSendBackRequest: SendApprovalSendBackRequest }>({
        query({ SendApprovalSendBackRequest }) {
          return {
            url: API_URLS.MOTOR_INVESTIGATION_APPROVAL.SEND_BACK,
            method: 'POST',
            body: SendApprovalSendBackRequest,
          };
        },
        invalidatesTags: ['MotorInvestigationApprovalData', 'MotorInvestigationCommentsData', 'MotorInvestigationApprovalInsights'],
        transformErrorResponse: transformApiError,
      }),
      setExternalInvestigationStatus: builders.mutation<number, { motorInvestigationFlowId: number }>({
        query({ motorInvestigationFlowId }) {
          return {
            url: API_URLS.MOTOR_INVESTIGATION_APPROVAL.SET_EXTERNAL_INVESTIGATION_STATUS,
            method: 'POST',
            body: { motorInvestigationFlowId },
          };
        },
        invalidatesTags: [],
        transformErrorResponse: transformApiError,
      }),

    };
  },
});

export const { useLazyFetchMotorInvestigationApprovalListQuery, useFetchMotorInvestigationApprovalListQuery,
  useFetchMotorInvestigationApprovalCommentsQuery, useLazyFetchMotorInvestigationApprovalCommentsQuery,
  useApproveInvestigationTicketMutation, useSendBackInvestigationTicketMutation,
  useSetExternalInvestigationStatusMutation,
  useLazyFetchMotorInvestigationApprovalListByIdQuery, useFetchMotorInvestigationApprovalListByIdQuery,
  useFetchMotorInvestigationApprovalInsightsQuery, useLazyFetchMotorInvestigationApprovalInsightsQuery
} = motorInvestigationApprovalApiSlice;
